import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Nav from '../components/nav';
import SEO from '../components/SEO';

// ── Inject styles once ────────────────────────────────────────────────────────
const injectStyles = () => {
  if (document.getElementById('pb-cast-styles')) return;
  const s = document.createElement('style');
  s.id = 'pb-cast-styles';
  s.textContent = `
    @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,700;1,400&family=Outfit:wght@300;400;500;600&display=swap');
    .pb-cast * { box-sizing: border-box; margin: 0; padding: 0; }
    @keyframes cast-blink   { 0%,100%{opacity:1} 50%{opacity:0} }
    @keyframes cast-float   { 0%{transform:translateY(0) scale(1);opacity:0} 15%{opacity:.5} 85%{opacity:.3} 100%{transform:translateY(-160px) scale(0.6);opacity:0} }
    @keyframes cast-fadein  { from{opacity:0;transform:translateY(20px)} to{opacity:1;transform:translateY(0)} }
    @keyframes cast-progress{ from{width:0%} to{width:100%} }
    @keyframes cast-strike  { from{width:0} to{width:100%} }
    @keyframes cast-pop     { 0%{transform:scale(0.85);opacity:0} 60%{transform:scale(1.04)} 100%{transform:scale(1);opacity:1} }
    @keyframes cast-scan    { 0%,100%{top:0%} 50%{top:85%} }
    @keyframes cast-pulse   { 0%,100%{box-shadow:0 0 0 0 rgba(239,68,68,.4)} 50%{box-shadow:0 0 0 12px rgba(239,68,68,0)} }

    .cast-cursor { display:inline-block;width:2px;height:.85em;background:#f59e0b;vertical-align:text-bottom;margin-left:2px;border-radius:1px;animation:cast-blink .75s step-end infinite; }
    .cast-particle { position:absolute;border-radius:50%;animation:cast-float linear infinite;pointer-events:none; }
    .cast-slide-enter { animation:cast-fadein .65s cubic-bezier(.16,1,.3,1) both; }
    .cast-progress-bar { animation:cast-progress linear both; }
    .cast-card:hover { transform:translateY(-5px);border-color:rgba(245,158,11,.3)!important; }
    .cast-prob-card:hover { transform:translateY(-3px); }
    .cast-sol-card:hover { transform:translateY(-4px);border-color:rgba(245,158,11,.35)!important; }
    .cast-ctrl:hover { background:rgba(245,158,11,.12)!important;color:#f59e0b!important;border-color:rgba(245,158,11,.4)!important; }
    .cast-btn-p:hover { transform:translateY(-3px);box-shadow:0 12px 40px rgba(245,158,11,.35); }
    .cast-btn-g:hover { border-color:#f59e0b!important;color:#f59e0b!important; }
    .cast-strike-through { position:relative;display:inline-block; }
    .cast-strike-through::after { content:'';position:absolute;top:50%;left:0;height:2px;background:#ef4444;animation:cast-strike .6s ease .5s both; }
    .cast-scan-line { position:absolute;left:0;right:0;height:2px;background:rgba(245,158,11,.5);animation:cast-scan 2.5s ease-in-out infinite; }
    .cast-dot-active { width:26px!important;border-radius:3px!important;background:#f59e0b!important; }
  `;
  document.head.appendChild(s);
};

// ── Slide data ────────────────────────────────────────────────────────────────
const SLIDES = [
  {
    id: 'history',
    kicker: 'Once Upon a Time in Casting',
    headline: 'Before ProducerBuddy,\ncasting was\na nightmare.',
    accent: '#ef4444',
    bg: 'radial-gradient(ellipse at 20% 60%, rgba(239,68,68,.07) 0%, transparent 55%)',
    type: 'history',
  },
  {
    id: 'pain-producer',
    kicker: 'The Director\'s burden',
    headline: 'You\'re a director.\nNot a recruitment\nagency.',
    body: 'Yet every casting cycle forces you to become one. Phone calls to agents. Endless email threads. Auditions that waste a full week. Talent that looks great on paper but disappears on set. Sound familiar?',
    accent: '#ef4444',
    bg: 'radial-gradient(ellipse at 80% 30%, rgba(239,68,68,.08) 0%, transparent 50%)',
    type: 'pain-director',
  },
  {
    id: 'pain-enthusiast',
    kicker: 'The Enthusiast\'s struggle',
    headline: 'You have the talent.\nNo one knows\nyou exist.',
    body: 'Hundreds of applications sent into void. Auditions that ghost you. Connections that go nowhere. You\'ve been knocking on every door in the industry — and none of them open.',
    accent: '#ef4444',
    bg: 'radial-gradient(ellipse at 20% 40%, rgba(239,68,68,.07) 0%, transparent 50%)',
    type: 'pain-enthusiast',
  },
  {
    id: 'solution',
    kicker: 'Built by Pilomation Engineers',
    headline: 'Every burden.\nSolved.\nIn minutes.',
    body: 'The Pilomation engineering and business team spent months studying how casting actually breaks — and built ProducerBuddy\'s AI Casting Platform to eliminate every single friction point.',
    accent: '#f59e0b',
    bg: 'radial-gradient(ellipse at 50% 0%, rgba(245,158,11,.08) 0%, transparent 60%)',
    type: 'solutions',
  },
  {
    id: 'connect',
    kicker: 'The bridge that changes everything',
    headline: 'Director meets talent.\nTalent meets\nopportunity.',
    body: 'ProducerBuddy is the living bridge between production houses and film enthusiasts. AI-matched. Skills-verified. No middlemen. No agents. No doors to knock on.',
    accent: '#f59e0b',
    bg: 'radial-gradient(ellipse at 70% 60%, rgba(245,158,11,.07) 0%, transparent 55%)',
    type: 'bridge',
  },
  {
    id: 'stats',
    kicker: 'Pilomation AI at work',
    headline: 'Days of casting\nbecomes\nminutes.',
    body: 'Our AI doesn\'t just search — it understands. Skills. Experience. Availability. Location. Performance history. Everything that matters to a real casting decision.',
    accent: '#f59e0b',
    bg: 'radial-gradient(ellipse at 30% 50%, rgba(245,158,11,.08) 0%, transparent 55%)',
    type: 'stats',
  },
  {
    id: 'cta',
    kicker: 'Your cast is waiting',
    headline: 'Stop searching.\nStart\nconnecting.',
    body: 'Whether you\'re a production house looking for your next lead actor, or an enthusiast waiting for their first real break — the platform designed for you is already here.',
    accent: '#f59e0b',
    bg: 'radial-gradient(ellipse at 50% 50%, rgba(245,158,11,.1) 0%, transparent 65%)',
    type: 'cta',
  },
];

const DURATION = 7000;
const TYPE_SPEED = 36;

// ── Historical problems ───────────────────────────────────────────────────────
const HISTORY_ITEMS = [
  { era: '1920s–80s', method: 'Word of mouth & studio rolls', time: 'Months', pain: 'Only insiders got roles' },
  { era: '1990s–2000s', method: 'Casting agencies & headshots', time: 'Weeks', pain: 'Expensive. Exclusive. Biased.' },
  { era: '2010s', method: 'Email blasts & Facebook groups', time: 'Days', pain: 'No verification. Zero structure.' },
  { era: 'Today (old way)', method: 'WhatsApp groups & cold calls', time: 'Still days', pain: 'Chaos. No matching. No tracking.' },
  { era: 'ProducerBuddy', method: 'AI-powered skills matching', time: 'Minutes', pain: null, highlight: true },
];

// ── Director pains ────────────────────────────────────────────────────────────
const DIRECTOR_PAINS = [
  { icon: '📋', pain: 'Sorting 500+ resumes manually for a single role' },
  { icon: '📞', pain: 'Calling agents who don\'t call back for days' },
  { icon: '🎭', pain: 'Auditions that run 3 days and yield no results' },
  { icon: '🗓️', pain: 'Scheduling conflicts discovered the day before shoot' },
  { icon: '💸', pain: 'Agency fees eating 15-20% of casting budget' },
  { icon: '🔍', pain: 'No way to verify claimed skills or experience' },
];

// ── Enthusiast pains ──────────────────────────────────────────────────────────
const ENTHUSIAST_PAINS = [
  { pain: 'Applications with zero response or feedback' },
  { pain: 'Travelling to auditions with no outcome' },
  { pain: 'Agents demanding 20% cuts before you earn anything' },
  { pain: 'No portfolio platform to showcase real skills' },
  { pain: 'Zero visibility — talent hidden behind geography' },
  { pain: 'Industry contacts needed just to get in the room' },
];

// ── Solutions ─────────────────────────────────────────────────────────────────
const SOLUTIONS = [
  { for: 'Director', icon: '🎬', problem: 'Days of screening resumes', solution: 'AI matches skills to your brief in seconds', color: '#f59e0b' },
  { for: 'Enthusiast', icon: '🎭', problem: 'Invisible to productions', solution: 'Your profile is discoverable by every active production', color: '#f59e0b' },
  { for: 'Director', icon: '📋', problem: 'Unverified talent claims', solution: 'Skill verification + showreel + history — all in one profile', color: '#f59e0b' },
  { for: 'Enthusiast', icon: '🚀', problem: 'No feedback on applications', solution: 'Real-time status updates for every audition', color: '#f59e0b' },
  { for: 'Director', icon: '💰', problem: '20% agency fees', solution: 'Zero agent cuts. Direct connection. Full control.', color: '#f59e0b' },
  { for: 'Enthusiast', icon: '🌍', problem: 'Location barriers', solution: 'Get discovered from anywhere — AI ignores geography', color: '#f59e0b' },
];

// ── Bridge features ───────────────────────────────────────────────────────────
const BRIDGE = [
  { side: 'Production House', items: ['Post a role with full brief', 'Receive AI-ranked candidates', 'Review profiles, showreels, history', 'Send audition invites in-platform', 'Communicate, schedule, confirm'], color: '#f59e0b', bg: 'rgba(245,158,11,.08)', border: 'rgba(245,158,11,.25)' },
  { side: 'Film Enthusiast', items: ['Build a verified skill profile', 'Discover roles matched to your skills', 'Apply with one click', 'Track application status live', 'Get cast — no middleman'], color: '#00c9a7', bg: 'rgba(0,201,167,.08)', border: 'rgba(0,201,167,.25)' },
];

// ── Stats ─────────────────────────────────────────────────────────────────────
const STATS_DATA = [
  { value: '< 5 min', label: 'Average time to first matched candidate' },
  { value: '0', label: 'Agency fees or middleman cuts' },
  { value: '100%', label: 'Skill-verified profiles' },
  { value: '1 platform', label: 'For directors AND enthusiasts' },
];

// ── AI features ───────────────────────────────────────────────────────────────
const AI_FEATURES = [
  'Natural language role briefs — describe the role, AI finds the person',
  'Skills matrix matching across 40+ film disciplines',
  'Availability cross-checking before suggesting candidates',
  'Showreel analysis for performance quality signals',
  'Location-aware matching with remote-ready flags',
  'Historical project scoring — see how they performed before',
];

// ── Typewriter hook ───────────────────────────────────────────────────────────
function useTypewriter(text, active) {
  const [displayed, setDisplayed] = useState('');
  const [done, setDone] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    clearInterval(ref.current);
    setDisplayed('');
    setDone(false);
    if (!active) return;
    let i = 0;
    const chars = text.split('');
    ref.current = setInterval(() => {
      if (i >= chars.length) { clearInterval(ref.current); setDone(true); return; }
      setDisplayed(p => p + chars[i++]);
    }, TYPE_SPEED);
    return () => clearInterval(ref.current);
  }, [text, active]);

  return { displayed, done };
}

function Headline({ text, active, accent }) {
  const { displayed, done } = useTypewriter(text, active);
  const lines = displayed.split('\n');
  return (
    <div style={{
      fontFamily: "'Cormorant Garamond', serif",
      fontSize: 'clamp(34px, 5.5vw, 60px)',
      fontWeight: 700, lineHeight: 1.1,
      letterSpacing: '-0.01em', color: '#f8fafc',
      minHeight: '3.8em', marginBottom: 28,
    }}>
      {lines.map((l, i) => <span key={i} style={{ display: 'block' }}>{l}</span>)}
      {!done && <span className="cast-cursor" style={{ background: accent }} />}
    </div>
  );
}

function Particles({ accent }) {
  const pts = useRef(Array.from({ length: 16 }, (_, i) => ({
    id: i, size: Math.random() * 4 + 1.5,
    left: Math.random() * 100, top: Math.random() * 100,
    dur: Math.random() * 10 + 7, delay: Math.random() * 7,
    amber: Math.random() > 0.4,
  }))).current;
  return <>
    {pts.map(p => (
      <div key={p.id} className="cast-particle" style={{
        width: p.size, height: p.size,
        background: p.amber ? accent : 'rgba(255,255,255,0.3)',
        left: `${p.left}%`, top: `${p.top}%`,
        animationDuration: `${p.dur}s`, animationDelay: `${p.delay}s`, opacity: 0,
      }} />
    ))}
  </>;
}

// ── Content components ────────────────────────────────────────────────────────
function HistoryContent({ visible }) {
  return (
    <div style={{ maxWidth: 760, margin: '0 auto', opacity: visible ? 1 : 0, transition: 'opacity .6s ease 1.2s' }}>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: "'Outfit', sans-serif" }}>
          <thead>
            <tr>
              {['Era', 'Method', 'Time taken', 'The real cost'].map(h => (
                <th key={h} style={{ padding: '10px 14px', fontSize: 11, letterSpacing: '2px', textTransform: 'uppercase', color: '#475569', textAlign: 'left', borderBottom: '1px solid rgba(255,255,255,.08)' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {HISTORY_ITEMS.map((item, i) => (
              <tr key={i} style={{
                background: item.highlight ? 'rgba(245,158,11,.08)' : 'transparent',
                transition: 'background .2s',
              }}>
                <td style={{ padding: '12px 14px', fontSize: 12, color: item.highlight ? '#f59e0b' : '#64748b', borderBottom: '1px solid rgba(255,255,255,.05)', fontWeight: item.highlight ? 600 : 400 }}>{item.era}</td>
                <td style={{ padding: '12px 14px', fontSize: 13, color: item.highlight ? '#fbbf24' : '#94a3b8', borderBottom: '1px solid rgba(255,255,255,.05)', fontWeight: item.highlight ? 600 : 400 }}>{item.method}</td>
                <td style={{ padding: '12px 14px', fontSize: 13, color: item.highlight ? '#f59e0b' : '#64748b', borderBottom: '1px solid rgba(255,255,255,.05)', fontWeight: item.highlight ? 700 : 400 }}>{item.time}</td>
                <td style={{ padding: '12px 14px', fontSize: 12, borderBottom: '1px solid rgba(255,255,255,.05)' }}>
                  {item.pain ? (
                    <span style={{ color: '#ef4444', display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ fontSize: 10 }}>✕</span> {item.pain}
                    </span>
                  ) : (
                    <span style={{ color: '#00c9a7', display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ fontSize: 12 }}>✓</span> <strong>AI handles it all</strong>
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function DirectorPainContent({ visible }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12, maxWidth: 720, margin: '28px auto 0', opacity: visible ? 1 : 0, transition: 'opacity .6s ease 1.3s' }}>
      {DIRECTOR_PAINS.map((p, i) => (
        <div key={i} className="cast-prob-card" style={{
          padding: '16px 14px',
          background: 'rgba(239,68,68,.06)', border: '1px solid rgba(239,68,68,.15)',
          borderRadius: 10, transition: 'transform .2s',
        }}>
          <div style={{ fontSize: 20, marginBottom: 8 }}>{p.icon}</div>
          <div style={{ fontSize: 12, color: '#94a3b8', lineHeight: 1.55, fontFamily: "'Outfit', sans-serif" }}>{p.pain}</div>
        </div>
      ))}
    </div>
  );
}

function EnthusiastPainContent({ visible }) {
  return (
    <div style={{ maxWidth: 600, margin: '28px auto 0', opacity: visible ? 1 : 0, transition: 'opacity .6s ease 1.3s' }}>
      {ENTHUSIAST_PAINS.map((p, i) => (
        <div key={i} style={{
          display: 'flex', alignItems: 'center', gap: 14, padding: '14px 18px',
          borderBottom: '1px solid rgba(255,255,255,.06)', fontFamily: "'Outfit', sans-serif",
        }}>
          <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'rgba(239,68,68,.1)', border: '1px solid rgba(239,68,68,.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: 14, color: '#ef4444', fontWeight: 700, animation: 'cast-pulse 2s infinite' }}>✕</div>
          <div className="cast-strike-through" style={{ fontSize: 14, color: '#94a3b8', position: 'relative' }}>
            {p.pain}
            <span style={{ position: 'absolute', top: '50%', left: 0, height: '1.5px', background: '#ef4444', width: '100%', transform: 'translateY(-50%)', animationDelay: `${i * 0.15}s` }} />
          </div>
        </div>
      ))}
    </div>
  );
}

function SolutionsContent({ visible }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 14, maxWidth: 740, margin: '28px auto 0', opacity: visible ? 1 : 0, transition: 'opacity .6s ease 1.3s' }}>
      {SOLUTIONS.map((s, i) => (
        <div key={i} className="cast-sol-card" style={{
          padding: '16px 18px',
          background: 'rgba(245,158,11,.05)', border: '1px solid rgba(245,158,11,.12)',
          borderRadius: 12, transition: 'transform .2s, border-color .2s',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
            <span style={{ fontSize: 18 }}>{s.icon}</span>
            <span style={{ fontSize: 10, letterSpacing: '1.5px', textTransform: 'uppercase', color: s.for === 'Director' ? '#f59e0b' : '#00c9a7', fontFamily: "'Outfit', sans-serif", fontWeight: 600 }}>for {s.for}</span>
          </div>
          <div style={{ fontSize: 11, color: '#64748b', textDecoration: 'line-through', marginBottom: 6, fontFamily: "'Outfit', sans-serif" }}>{s.problem}</div>
          <div style={{ fontSize: 13, color: '#e2e8f0', lineHeight: 1.5, fontFamily: "'Outfit', sans-serif" }}>{s.solution}</div>
        </div>
      ))}
    </div>
  );
}

function BridgeContent({ visible }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', gap: 0, maxWidth: 760, margin: '28px auto 0', alignItems: 'center', opacity: visible ? 1 : 0, transition: 'opacity .6s ease 1.3s' }}>
      {BRIDGE.map((side, i) => (
        <React.Fragment key={i}>
          <div style={{ padding: '24px 20px', background: side.bg, border: `1px solid ${side.border}`, borderRadius: 14 }}>
            <div style={{ fontSize: 11, letterSpacing: '2px', textTransform: 'uppercase', color: side.color, marginBottom: 14, fontFamily: "'Outfit', sans-serif", fontWeight: 600 }}>{side.side}</div>
            {side.items.map((item, j) => (
              <div key={j} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 10 }}>
                <div style={{ width: 5, height: 5, borderRadius: '50%', background: side.color, marginTop: 5, flexShrink: 0 }} />
                <span style={{ fontSize: 12, color: '#94a3b8', lineHeight: 1.5, fontFamily: "'Outfit', sans-serif" }}>{item}</span>
              </div>
            ))}
          </div>
          {i === 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '0 20px', gap: 8 }}>
              <div style={{ width: 48, height: 48, borderRadius: '50%', background: 'rgba(245,158,11,.15)', border: '1px solid rgba(245,158,11,.4)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div style={{ fontSize: 20 }}>⚡</div>
              </div>
              <div style={{ fontSize: 10, letterSpacing: '1px', textTransform: 'uppercase', color: '#f59e0b', fontFamily: "'Outfit', sans-serif", textAlign: 'center' }}>AI<br/>match</div>
            </div>
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

function StatsContent({ visible }) {
  return (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 14, maxWidth: 600, margin: '28px auto 0', opacity: visible ? 1 : 0, transition: 'opacity .6s ease 1.2s' }}>
        {STATS_DATA.map((s, i) => (
          <div key={i} style={{ padding: '24px 20px', textAlign: 'center', background: 'rgba(245,158,11,.06)', border: '1px solid rgba(245,158,11,.15)', borderRadius: 14 }}>
            <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 'clamp(28px,5vw,42px)', fontWeight: 700, color: '#f59e0b', lineHeight: 1, marginBottom: 8 }}>{s.value}</div>
            <div style={{ fontSize: 12, color: '#64748b', fontFamily: "'Outfit', sans-serif", lineHeight: 1.5 }}>{s.label}</div>
          </div>
        ))}
      </div>
      <div style={{ maxWidth: 580, margin: '20px auto 0', opacity: visible ? 1 : 0, transition: 'opacity .6s ease 1.6s' }}>
        {AI_FEATURES.map((f, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '10px 0', borderBottom: '1px solid rgba(255,255,255,.05)' }}>
            <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#f59e0b', marginTop: 5, flexShrink: 0 }} />
            <span style={{ fontSize: 13, color: '#94a3b8', fontFamily: "'Outfit', sans-serif", lineHeight: 1.5 }}>{f}</span>
          </div>
        ))}
      </div>
    </>
  );
}

function CTAContent({ visible, navigate }) {
  return (
    <div style={{ opacity: visible ? 1 : 0, transition: 'opacity .6s ease 1.5s' }}>
      <div style={{ display: 'flex', gap: 40, justifyContent: 'center', margin: '28px 0', flexWrap: 'wrap' }}>
        {[
          { label: 'I\'m a Production House', sub: 'Find talent in minutes', path: '/production-house', color: '#f59e0b' },
          { label: 'I\'m a Film Enthusiast', sub: 'Get discovered today', path: '/film-enthusiast', color: '#00c9a7' },
        ].map((item, i) => (
          <div key={i} onClick={() => navigate(item.path)} style={{
            padding: '24px 32px', background: `rgba(${i === 0 ? '245,158,11' : '0,201,167'},.07)`,
            border: `1px solid ${item.color}30`, borderRadius: 14,
            cursor: 'pointer', textAlign: 'center', minWidth: 220,
            transition: 'transform .2s, border-color .2s',
          }}
          onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.borderColor = item.color + '70'; }}
          onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.borderColor = item.color + '30'; }}>
            <div style={{ fontSize: 15, fontWeight: 600, color: item.color, marginBottom: 6, fontFamily: "'Outfit', sans-serif" }}>{item.label}</div>
            <div style={{ fontSize: 12, color: '#64748b', fontFamily: "'Outfit', sans-serif" }}>{item.sub}</div>
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 14, justifyContent: 'center', flexWrap: 'wrap' }}>
        <button className="cast-btn-p" onClick={() => navigate('/signup')} style={{
          padding: '14px 32px', background: '#f59e0b', color: '#0a0a0a',
          border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 700,
          cursor: 'pointer', transition: 'transform .2s, box-shadow .2s',
          fontFamily: "'Outfit', sans-serif", letterSpacing: '.3px',
        }}>
          Create free account →
        </button>
        <button className="cast-btn-g" onClick={() => navigate('/cast')} style={{
          padding: '14px 32px', background: 'transparent', color: '#94a3b8',
          border: '1px solid rgba(255,255,255,.15)', borderRadius: 10, fontSize: 14,
          cursor: 'pointer', transition: 'all .2s', fontFamily: "'Outfit', sans-serif",
        }}>
          Explore casting platform
        </button>
      </div>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
const CastingGuide = () => {
  const navigate = useNavigate();
  const [current, setCurrent] = useState(0);
  const [paused, setPaused] = useState(false);
  const [visible, setVisible] = useState(false);
  const [progKey, setProgKey] = useState(0);
  const timerRef = useRef(null);

  useEffect(() => { injectStyles(); setVisible(true); }, []);

  const goTo = useCallback((n) => {
    const next = ((n % SLIDES.length) + SLIDES.length) % SLIDES.length;
    setVisible(false);
    setTimeout(() => { setCurrent(next); setProgKey(k => k + 1); setVisible(true); }, 300);
  }, []);

  useEffect(() => {
    if (paused) { clearTimeout(timerRef.current); return; }
    timerRef.current = setTimeout(() => goTo(current + 1), DURATION);
    return () => clearTimeout(timerRef.current);
  }, [current, paused, goTo]);

  const slide = SLIDES[current];

  return (
    <div className="pb-cast" style={{ minHeight: '100vh', background: '#060810', color: '#e8edf2', display: 'flex', flexDirection: 'column' }}>
      <SEO
        title="Complete Film Casting Guide"
        description="The complete guide to film casting on ProducerBuddy — discover talent, manage auditions, and build your ideal cast using AI-assisted tools."
        url="https://producerbuddy.pilomation.com/casting-guide"
      />
      <Nav />

      {/* Progress */}
      {!paused && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, height: 3, zIndex: 200, background: 'rgba(255,255,255,.06)' }}>
          <div key={progKey} className="cast-progress-bar" style={{
            height: '100%',
            background: `linear-gradient(90deg, ${slide.accent}, ${slide.accent === '#ef4444' ? '#f97316' : '#00c9a7'})`,
            animationDuration: `${DURATION}ms`, animationTimingFunction: 'linear', animationFillMode: 'both',
          }} />
        </div>
      )}

      {/* Slide area */}
      <div style={{ flex: 1, position: 'relative', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ position: 'absolute', inset: 0, background: slide.bg, transition: 'background .8s', zIndex: 0 }} />
        <div style={{ position: 'absolute', inset: 0, zIndex: 1 }}><Particles accent={slide.accent} /></div>

        <div className={visible ? 'cast-slide-enter' : ''} style={{
          position: 'relative', zIndex: 2, maxWidth: 900, width: '100%',
          padding: '50px 36px', textAlign: 'center',
          opacity: visible ? 1 : 0,
        }}>
          {/* Kicker */}
          <div style={{
            fontSize: 11, letterSpacing: '3px', textTransform: 'uppercase',
            color: slide.accent, marginBottom: 20, fontFamily: "'Outfit', sans-serif",
            fontWeight: 500, opacity: visible ? 1 : 0, transition: 'opacity .5s ease .2s',
          }}>{slide.kicker}</div>

          <Headline text={slide.headline} active={visible} accent={slide.accent} />

          {slide.body && (
            <p style={{
              fontSize: 15, lineHeight: 1.8, color: '#94a3b8',
              maxWidth: 600, margin: '0 auto 8px', fontFamily: "'Outfit', sans-serif",
              opacity: visible ? 1 : 0, transition: 'opacity .6s ease 1.2s',
            }}>{slide.body}</p>
          )}

          {slide.type === 'history' && <HistoryContent visible={visible} />}
          {slide.type === 'pain-director' && <DirectorPainContent visible={visible} />}
          {slide.type === 'pain-enthusiast' && <EnthusiastPainContent visible={visible} />}
          {slide.type === 'solutions' && <SolutionsContent visible={visible} />}
          {slide.type === 'bridge' && <BridgeContent visible={visible} />}
          {slide.type === 'stats' && <StatsContent visible={visible} />}
          {slide.type === 'cta' && <CTAContent visible={visible} navigate={navigate} />}
        </div>
      </div>

      {/* Nav */}
      <div style={{ padding: '18px 0 14px', background: 'rgba(6,8,16,.92)', backdropFilter: 'blur(12px)', zIndex: 10 }}>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginBottom: 12 }}>
          {SLIDES.map((sl, i) => (
            <div key={i} className={i === current ? 'cast-dot-active' : ''} onClick={() => goTo(i)} style={{
              width: 6, height: 6, borderRadius: '50%',
              background: 'rgba(255,255,255,.18)', cursor: 'pointer', transition: 'all .3s',
            }} />
          ))}
        </div>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'center', alignItems: 'center' }}>
          {['← prev', paused ? '▶ play' : '⏸ pause', 'next →'].map((label, i) => (
            <button key={i} className="cast-ctrl" onClick={() => {
              if (i === 0) goTo(current - 1);
              else if (i === 1) setPaused(p => !p);
              else goTo(current + 1);
            }} style={{
              background: 'rgba(255,255,255,.06)', border: '1px solid rgba(255,255,255,.1)',
              color: '#94a3b8', padding: '8px 20px', borderRadius: 6, fontSize: 12,
              cursor: 'pointer', transition: 'all .2s', fontFamily: "'Outfit', sans-serif",
            }}>{label}</button>
          ))}
        </div>
        <div style={{ textAlign: 'center', marginTop: 10, fontSize: 11, color: '#1e293b', fontFamily: "'Outfit', sans-serif", letterSpacing: '1px' }}>
          {current + 1} / {SLIDES.length} — {slide.kicker}
        </div>
      </div>
    </div>
  );
};

export default CastingGuide;
